import { useContext, useEffect, useRef, useState } from "react"

import { EditorView, basicSetup } from "codemirror"
import { EditorState, Transaction, Compartment } from "@codemirror/state"
import { keymap, ViewPlugin } from "@codemirror/view"
import { indentWithTab } from "@codemirror/commands"
import { indentUnit } from "@codemirror/language"
import { pythonLanguage } from "@codemirror/lang-python"
import { cppLanguage } from "@codemirror/lang-cpp"
import { javaLanguage } from "@codemirror/lang-java"
import { html } from "@codemirror/lang-html"
import { css } from "@codemirror/lang-css"
import { javascript } from "@codemirror/lang-javascript"

import { useRegion } from '../hooks/useRegion'
import { TelemetryContext } from '../context/TelemetryContext'

import { CODE_EVENTS, EDITOR_EVENTS, REGIONS } from '../constants/telemetryEvents'

import '../css/workspace.scss'

const LANGUAGE_EXTENSIONS = {
  python: pythonLanguage,
  c: cppLanguage,
  java: javaLanguage,
}

const ALL_WEB_DEV_FILES = ['html', 'css', 'js']
const WEB_DEV_FILE_LABELS = { html: 'HTML', css: 'CSS', js: 'JavaScript' }

const DEFAULT_WEB_DEV_TEMPLATE = {
  html: "<h1>Hello World</h1>\n<p>Welcome to Web Development!</p>\n<button id=\"btn\">Click Me</button>",
  css: "body {\n  font-family: sans-serif;\n  text-align: center;\n  padding-top: 50px;\n}\nbutton {\n  padding: 10px 20px;\n  font-size: 16px;\n}",
  js: "document.getElementById('btn').addEventListener('click', () => {\n  alert('Button clicked!');\n});"
}

const getWebDevExtension = (file) => {
  if (file === 'html') return html()
  if (file === 'css') return css()
  if (file === 'js') return javascript()
  return html()
}

// Helper to map CodeMirror events to Telemetry Event Names
const getTelemetryEventType = (cmEventType, changeText = '') => {
  if (cmEventType === "input") {
    if (changeText === '\t' || changeText === '    ') return CODE_EVENTS.INDENT
    return CODE_EVENTS.TYPE
  }

  // Catch all input.type variants including compose
  if (cmEventType.startsWith("input.type")) return CODE_EVENTS.TYPE

  switch (cmEventType) {
    case "input.paste": return CODE_EVENTS.PASTE
    case "input.indent": return CODE_EVENTS.INDENT
    case "delete.backward": return CODE_EVENTS.DELETE
    case "delete.forward": return CODE_EVENTS.DELETE
    case "delete.selection": return CODE_EVENTS.DELETE_SELECTION
    case "delete.cut": return CODE_EVENTS.CUT
    case "undo": return CODE_EVENTS.UNDO
    case "redo": return CODE_EVENTS.REDO
    default: return CODE_EVENTS.UNKNOWN
  }
}

// ── Study conditions: the cursor and keys between edits ────────────────────────────────
// Only study condition sessions record these (TelemetryContext's isStudy); for every other
// session the editor is set up exactly as before.

const CURSOR_DEBOUNCE_MS = 150

// Keys EDITOR_KEY records on their own: the ones that move the cursor or edit without
// typing a character. Printable keys are already recorded as the edits they make.
const EDITOR_KEY_NAMES = new Set([
  'Enter', 'Tab', 'Backspace', 'Delete', 'Escape',
  'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown',
])

// Not key presses worth a record: a modifier pressed on its own, and the keys that only
// build a character (dead keys, IME composition).
const IGNORED_KEY_NAMES = new Set([
  'Control', 'Meta', 'Alt', 'AltGraph', 'Shift', 'CapsLock', 'Fn', 'FnLock', 'Hyper', 'Super', 'OS',
  'Dead', 'Process', 'Unidentified',
])

// EDITOR_KEY's payload for a keydown in the editor, or null when it is not recorded: the
// named keys above, and any key held with Ctrl, Cmd or Alt (shortcuts).
export const editorKeyPayload = (event) => {
  if (!event || event.isComposing || IGNORED_KEY_NAMES.has(event.key)) return null
  const modified = Boolean(event.ctrlKey || event.metaKey || event.altKey)
  if (!modified && !EDITOR_KEY_NAMES.has(event.key)) return null
  return {
    key: event.key,
    ctrl: Boolean(event.ctrlKey),
    meta: Boolean(event.metaKey),
    shift: Boolean(event.shiftKey),
    alt: Boolean(event.altKey),
    repeat: Boolean(event.repeat),
  }
}

// CURSOR_SELECT's payload: every selection range (CodeMirror allows several cursors) and
// the index of the main one.
export const selectionPayload = (selection) => ({
  ranges: selection.ranges.map((range) => ({ anchor: range.anchor, head: range.head })),
  main: selection.mainIndex,
})

// The main range alone, added to each CODE_* payload.
export const mainSelectionPayload = (selection) => ({ anchor: selection.main.anchor, head: selection.main.head })

// emit(type, payload) logs an event; getFile() names the web-dev file on screen (undefined
// for single-file sessions). Logs CURSOR_SELECT once the cursor or selection has rested for
// CURSOR_DEBOUNCE_MS without an edit (an edit's CODE_* event carries the selection after
// it), and EDITOR_KEY for every recorded keydown. An event observer, not a handler: it
// sees every key, even the ones a keymap (Enter, Tab, arrows...) then handles. Also used by
// the Reasoning Gap quiz editor (ReasoningGapStudent).
export const studyEditorTelemetry = (emit, getFile) => [
  ViewPlugin.fromClass(class {
    constructor(view) {
      // The selection the record already has: the last one logged, or the one after the
      // last edit.
      this.recorded = view.state.selection
      this.pending = null
      this.timer = null
    }

    // Plugins see an update before the update listeners do, so a cursor move made just
    // before an edit is logged ahead of that edit's CODE_* event, against the text it was
    // made in.
    update(update) {
      if (update.docChanged) {
        this.flush()
        this.recorded = update.state.selection
        return
      }
      if (!update.selectionSet) return
      this.pending = { selection: update.state.selection, at: Date.now(), file: getFile() }
      clearTimeout(this.timer)
      this.timer = setTimeout(() => this.flush(), CURSOR_DEBOUNCE_MS)
    }

    flush() {
      clearTimeout(this.timer)
      this.timer = null
      const pending = this.pending
      this.pending = null
      if (!pending || pending.selection.eq(this.recorded)) return
      this.recorded = pending.selection
      emit(EDITOR_EVENTS.CURSOR_SELECT, {
        ...selectionPayload(pending.selection),
        active_file: pending.file,
        timestamp: pending.at,
      })
    }

    destroy() {
      this.flush()
    }
  }),
  EditorView.domEventObservers({
    keydown(event) {
      const payload = editorKeyPayload(event)
      if (payload) emit(EDITOR_EVENTS.KEY, { ...payload, active_file: getFile() })
    },
  }),
]

const EDITOR_THEME = EditorView.theme({
  "&": {
    height: "100%",
    fontSize: "14px",
    color: "#1f2937",
  },
  ".cm-content": {
    padding: "16px",
    minHeight: "100%",
    color: "#1f2937",
    fontFamily: "Monaco, Menlo, monospace"
  },
  ".cm-focused": {
    outline: "none",
  },
  ".cm-editor": {
    height: "100%",
    minHeight: "0px",
    backgroundColor: "#ffffff",
  },
  ".cm-scroller": {
    height: "100%",
    minHeight: "0px",
    overflow: "auto"
  },
  ".cm-line": {
    color: "#1f2937",
  },
  ".cm-cursor": {
    borderLeftColor: "#1f2937",
  },
  ".cm-selectionBackground": {
    backgroundColor: "#e5e7eb !important",
  },
  // Syntax Highlighting Colors
  ".cm-keyword": { color: "#7c3aed" },
  ".cm-string": { color: "#059669" },
  ".cm-comment": { color: "#6b7280" },
  ".cm-number": { color: "#dc2626" },
})

// One CodeMirror pane, fully self-contained (its own EditorView, its own per-keystroke
// telemetry classification, its own select/copy tracking, its own mouse region). Used by
// 'columns' mode below to mount all of a GUI session's enabledFiles simultaneously (a JS
// Bin-style layout) with the exact same telemetry fidelity - CODE_TYPE/CODE_PASTE/CODE_UNDO/
// etc., each correctly tagged with active_file - that the single-pane/tabbed mode above
// already has for its one currently-visible file. Deliberately not shared with the tabbed
// mode's implementation: that mode's tab-switching (reconfiguring one shared EditorView's
// language/content/readonly compartments on switchTab) is a meaningfully different state
// machine, and duplicating the ~40 lines of EditorView setup here is a smaller risk than
// reworking that already-shipped, heavily-used path to accommodate both shapes.
function CodeMirrorPane({ file, initialValue, editable, onChange, onTelemetry }) {
  const editorRef = useRef(null)
  const editorViewRef = useRef(null)
  const propsRef = useRef({ onChange, onTelemetry })

  useEffect(() => {
    propsRef.current = { onChange, onTelemetry }
  }, [onChange, onTelemetry])

  // Unique id per pane (not the shared REGIONS.CODE_EDITOR constant every other workspace
  // region uses) so mouse-region attribution can tell which of the 3 simultaneous panes a
  // MOUSE_MOVE/MOUSE_CLICK actually happened over, instead of all 3 colliding on one id and
  // only the last-registered pane's bounding box ever being reflected.
  useRegion(`${REGIONS.CODE_EDITOR}_${file}`, editorRef)

  useEffect(() => {
    if (!editorRef.current || editorViewRef.current) return

    const state = EditorState.create({
      doc: initialValue || '',
      extensions: [
        basicSetup,
        getWebDevExtension(file),
        EditorState.readOnly.of(!editable),
        EditorView.editable.of(editable),
        indentUnit.of("    "),
        keymap.of([indentWithTab]),
        EditorView.updateListener.of((update) => {
          if (!update.docChanged) return
          const newCode = update.state.doc.toString()

          let rawEventType = "unknown"
          for (const tr of update.transactions) {
            const event = tr.annotation(Transaction.userEvent)
            if (event) { rawEventType = event; break }
          }

          const changeDetails = []
          update.changes.iterChanges((fromA, toA, _fromB, _toB, inserted) => {
            changeDetails.push({ from: fromA, to: toA, text: inserted.toString() })
          })
          const firstChangeText = changeDetails[0]?.text || ''
          const telemetryEventName = getTelemetryEventType(rawEventType, firstChangeText)

          const { onChange, onTelemetry } = propsRef.current
          onChange?.(newCode)
          onTelemetry?.(telemetryEventName, {
            raw_action: rawEventType,
            changes: changeDetails,
            code: newCode,
            active_file: file,
            timestamp: Date.now(),
          })
        }),
        EDITOR_THEME,
      ],
    })

    editorViewRef.current = new EditorView({ state, parent: editorRef.current })

    return () => {
      editorViewRef.current?.destroy()
      editorViewRef.current = null
    }
    // Deliberately mounts once per pane instance - 'columns' mode force-remounts a pane
    // (via a key change, see resetKey below) rather than reconfiguring it in place, so this
    // effect doesn't need to react to initialValue/editable changing after mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Select/copy tracking, scoped to this pane's own container - window.getSelection() is a
  // global API, but the mouseup/keyup/copy listeners are only attached to this pane's DOM
  // node, so only genuine selections/copies made inside *this* pane fire here.
  useEffect(() => {
    const el = editorRef.current
    if (!el) return

    let lastSelection = ''

    const handleSelectionChange = () => {
      const selectedText = window.getSelection().toString()
      if (selectedText && selectedText !== lastSelection) {
        lastSelection = selectedText
        propsRef.current.onTelemetry?.(CODE_EVENTS.SELECT, {
          selected_text: selectedText,
          active_file: file,
          timestamp: Date.now()
        })
      } else if (!selectedText) {
        lastSelection = ''
      }
    }

    const handleCopy = () => {
      const selectedText = window.getSelection().toString()
      if (selectedText) {
        propsRef.current.onTelemetry?.(CODE_EVENTS.COPY, {
          selected_text: selectedText,
          active_file: file,
          timestamp: Date.now()
        })
      }
    }

    el.addEventListener('mouseup', handleSelectionChange)
    el.addEventListener('keyup', handleSelectionChange)
    el.addEventListener('copy', handleCopy)

    return () => {
      el.removeEventListener('mouseup', handleSelectionChange)
      el.removeEventListener('keyup', handleSelectionChange)
      el.removeEventListener('copy', handleCopy)
    }
  }, [file])

  return <div ref={editorRef} style={{ width: '100%', height: '100%' }} />
}

export default function CodeEditor({
  initialCode,
  onCodeChange,
  onTelemetry,
  language = 'python',
  isWebDev = false,
  enabledFiles = ['html', 'css', 'js'],
  starterCode,
  layout = 'tabs',
  // When true, the tab bar only shows enabledFiles instead of all three web-dev files
  // locked/unlocked - a genuinely single-file editor (no CSS/JS tabs at all, not even
  // read-only ones) rather than the locked-tab convention every other web-dev type uses.
  hideDisabledTabs = false,
  // Files to show as a locked, read-only tab even when hideDisabledTabs is on - for
  // InitialFinalCSS, students edit CSS only, but were left with no way to see the markup
  // they're writing selectors against (the AI assistant is shown it as read-only context,
  // but the student's own screen never was). JS is deliberately left hidden there (it's
  // always empty for this type) rather than making this the same as hideDisabledTabs=false.
  extraReadOnlyFiles = [],
  // Optional prominent label shown to the left of the file tabs (e.g. "Your Code"). Added
  // for Initial/Final Comparison: with Initial/Target renders and a Web Preview all on
  // screen at once, instructors found it wasn't obvious at a glance which pane was actually
  // the student's editable input - null for every other type, so their tab bar is unchanged.
  paneTitle = null,
  // Renders 'columns' mode's three panes stacked vertically instead of side by side, each
  // permanently visible (no per-file show/hide toggle row - unlike JSBin, nothing here is
  // meant to be hidden). Added for InitialFinalJS's CodePen-style editor. Reuses every bit
  // of columns mode's shared-state/telemetry/reset-template logic unchanged - only the
  // pane container's flex direction (and which chrome renders above it) differs.
  stacked = false,
}) {
  const editorRef = useRef(null)
  const editorViewRef = useRef(null)

  const propsRef = useRef({ onCodeChange, onTelemetry })

  // Study conditions also record the cursor, the selection and editor keys (TelemetryContext's
  // isStudy). Optional: the editor still works outside a TelemetryProvider.
  const telemetry = useContext(TelemetryContext)
  const studyTelemetryRef = useRef(Boolean(telemetry?.isStudy))
  useEffect(() => {
    studyTelemetryRef.current = Boolean(telemetry?.isStudy)
  }, [telemetry?.isStudy])

  // Web Dev multi-file support
  const [activeTab, setActiveTab] = useState(enabledFiles[0] || 'html')
  const activeTabRef = useRef(activeTab)
  useEffect(() => {
    activeTabRef.current = activeTab
  }, [activeTab])

  const codesRef = useRef({ html: "", css: "", js: "" })
  const isWebDevInitialized = useRef(false)

  // 'columns' mode remounts all 3 panes (via changing this key) on Reset Template, instead
  // of reconfiguring each EditorView's content in place - simpler, and Reset already
  // discards all current content anyway (the confirm() dialog says so).
  const [resetKey, setResetKey] = useState(0)

  // Which of the 3 code panes are currently shown, in 'columns' mode - JS Bin's own toolbar
  // buttons toggle a pane's visibility and the remaining visible panes redistribute the
  // available width, rather than opening a fixed layout. Panes stay mounted when hidden
  // (display: none, not unmounted) so toggling one back on doesn't lose its undo history,
  // scroll position, or cursor.
  const [visiblePanes, setVisiblePanes] = useState({ html: true, css: true, js: true })

  if (isWebDev && !isWebDevInitialized.current) {
    const defaultTemplate = starterCode ? JSON.parse(starterCode) : DEFAULT_WEB_DEV_TEMPLATE
    let didFallback = false
    try {
      if (initialCode) {
        const parsed = JSON.parse(initialCode)
        if (parsed && typeof parsed === 'object' && 'html' in parsed && 'css' in parsed && 'js' in parsed) {
          codesRef.current = parsed
        } else {
          throw new Error("Invalid structure")
        }
      } else {
        codesRef.current = defaultTemplate
        didFallback = true
      }
    } catch (e) {
      codesRef.current = defaultTemplate
      didFallback = true
    }

    if (didFallback && onCodeChange) {
      onCodeChange(JSON.stringify(defaultTemplate))
    }
    isWebDevInitialized.current = true
  }

  const languageCompartmentRef = useRef(new Compartment())
  const readOnlyCompartmentRef = useRef(new Compartment())

  const getReadOnlyExtension = (tab) => {
    const readOnly = isWebDev && !enabledFiles.includes(tab)
    return [EditorState.readOnly.of(readOnly), EditorView.editable.of(!readOnly)]
  }

  useEffect(() => {
    propsRef.current = { onCodeChange, onTelemetry }
  }, [onCodeChange, onTelemetry])

  // Tells telemetry the code this editor opened with (SESSION_START's `code`), in the shape
  // the CODE_* events send it. Once, at mount: after a reload that is the student's saved
  // work, which the edits that follow are made against.
  useEffect(() => {
    telemetry?.noteInitialCode?.(isWebDev ? JSON.stringify(codesRef.current) : (initialCode || ""))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useRegion(REGIONS.CODE_EDITOR, editorRef)

  const isColumnsMode = isWebDev && layout === 'columns'

  useEffect(() => {
    if (isColumnsMode) return
    if (editorRef.current && !editorViewRef.current) {

      const langExtension = isWebDev
        ? getWebDevExtension(activeTab)
        : (LANGUAGE_EXTENSIONS[language] || pythonLanguage)

      const docVal = isWebDev
        ? codesRef.current[activeTab]
        : (initialCode || "")

      const state = EditorState.create({
        doc: docVal,
        extensions: [
          basicSetup,
          isWebDev ? languageCompartmentRef.current.of(langExtension) : langExtension,
          readOnlyCompartmentRef.current.of(getReadOnlyExtension(activeTab)),
          indentUnit.of("    "),
          keymap.of([indentWithTab]),

          EditorView.updateListener.of((update) => {
            if (update.docChanged) {
              const newCode = update.state.doc.toString()

              /* Reference: CodeMirror Transaction.userEvent types
                https://codemirror.net/docs/ref/#state.Transaction^userEvent
              */

              // 1. Get user's intent
              let rawEventType = "unknown"
              for (const tr of update.transactions) {
                  const event = tr.annotation(Transaction.userEvent)
                  if (event) {
                      rawEventType = event
                      break
                  }
              }

              // 2. Extract Exact Text Changes (The "Diff")
              const changeDetails = []
              update.changes.iterChanges((fromA, toA, _fromB, _toB, inserted) => {
                  const text = inserted.toString()
                  changeDetails.push({
                      from: fromA,
                      to: toA,
                      text: text
                  })
              })

              // 3. Get first change text for event type detection
              const firstChangeText = changeDetails[0]?.text || ''

              // 4. Map to Event Name (e.g. "CODE_PASTE")
              const telemetryEventName = getTelemetryEventType(rawEventType, firstChangeText)

              // 5. Package Telemetry
              const telemetryPayload = {
                raw_action: rawEventType,
                changes: changeDetails,
                code: newCode,
                timestamp: Date.now(),
                // Study conditions: the main cursor/selection after the edit.
                ...(studyTelemetryRef.current ? { selection: mainSelectionPayload(update.state.selection) } : {})
              }

              // 6. Update code state and log telemetry
              const { onCodeChange, onTelemetry } = propsRef.current

              let codeToSend = newCode
              if (isWebDev) {
                codesRef.current[activeTabRef.current] = newCode
                codeToSend = JSON.stringify(codesRef.current)
              }

              if (onCodeChange) {
                onCodeChange(codeToSend)
              }

              if (onTelemetry) {
                const payload = {
                  ...telemetryPayload,
                  code: codeToSend,
                  active_file: isWebDev ? activeTabRef.current : undefined
                }
                onTelemetry(telemetryEventName, payload)
              }
            }
          }),

          // Study conditions only: the cursor and keys between edits.
          ...(studyTelemetryRef.current
            ? studyEditorTelemetry(
              (type, payload) => propsRef.current.onTelemetry?.(type, payload),
              () => (isWebDev ? activeTabRef.current : undefined)
            )
            : []),

          EDITOR_THEME,
        ],
      })

      editorViewRef.current = new EditorView({
        state,
        parent: editorRef.current,
      })
    }

    // Cleanup
    return () => {
      if (editorViewRef.current) {
        editorViewRef.current.destroy()
        editorViewRef.current = null
      }
    }
  }, [isWebDev, language, isColumnsMode])

  // Track selection and copy events
  useEffect(() => {
    if (isColumnsMode) return
    const el = editorRef.current
    if (!el) return

    let lastSelection = ''

    const handleSelectionChange = () => {
      const selectedText = window.getSelection().toString()
      if (selectedText && selectedText !== lastSelection) {
        lastSelection = selectedText
        propsRef.current.onTelemetry?.(CODE_EVENTS.SELECT, {
          selected_text: selectedText,
          timestamp: Date.now()
        })
      } else if (!selectedText) {
        lastSelection = ''
      }
    }

    const handleCopy = () => {
      const selectedText = window.getSelection().toString()
      if (selectedText) {
        propsRef.current.onTelemetry?.(CODE_EVENTS.COPY, {
          selected_text: selectedText,
          timestamp: Date.now()
        })
      }
    }

    el.addEventListener('mouseup', handleSelectionChange)
    el.addEventListener('keyup', handleSelectionChange)
    el.addEventListener('copy', handleCopy)

    return () => {
      el.removeEventListener('mouseup', handleSelectionChange)
      el.removeEventListener('keyup', handleSelectionChange)
      el.removeEventListener('copy', handleCopy)
    }
  }, [isColumnsMode])

  const switchTab = (newTab) => {
    if (!editorViewRef.current) return

    propsRef.current.onTelemetry?.(CODE_EVENTS.TAB_SWITCH, {
      from: activeTab,
      to: newTab,
      timestamp: Date.now(),
    })

    const currentCode = editorViewRef.current.state.doc.toString()
    codesRef.current[activeTab] = currentCode

    const nextCode = codesRef.current[newTab] || ""
    const nextExtension = getWebDevExtension(newTab)

    // The CodeMirror update listener fires synchronously inside dispatch()
    // and reads activeTabRef.current to know which tab's code just changed.
    // It must already point at newTab before we dispatch the tab-swap, or
    // the listener attributes nextCode's content to the *old* tab and
    // clobbers it (setActiveTab's ref-sync effect wouldn't run until after
    // this function returns, which is too late).
    activeTabRef.current = newTab
    setActiveTab(newTab)

    editorViewRef.current.dispatch({
      changes: { from: 0, to: editorViewRef.current.state.doc.length, insert: nextCode },
      effects: [
        languageCompartmentRef.current.reconfigure(nextExtension),
        readOnlyCompartmentRef.current.reconfigure(getReadOnlyExtension(newTab)),
      ]
    })
  }

  const resetToDefault = () => {
    if (window.confirm("Are you sure you want to reset the editor to the default starter template? This will overwrite your current code.")) {
      const defaultTemplate = starterCode ? JSON.parse(starterCode) : DEFAULT_WEB_DEV_TEMPLATE
      codesRef.current = { ...defaultTemplate }
      if (onCodeChange) {
        onCodeChange(JSON.stringify(defaultTemplate))
      }
      if (isColumnsMode) {
        setResetKey((k) => k + 1)
        return
      }
      if (editorViewRef.current) {
        editorViewRef.current.dispatch({
          changes: { from: 0, to: editorViewRef.current.state.doc.length, insert: defaultTemplate[activeTab] }
        })
      }
    }
  }

  if (isColumnsMode) {
    const handlePaneChange = (file) => (newValue) => {
      codesRef.current[file] = newValue
      propsRef.current.onCodeChange?.(JSON.stringify(codesRef.current))
    }

    // JS Bin-style pane toggles: clicking a file's button shows/hides that pane, and the
    // remaining visible panes redistribute the available width evenly (via flex: 1 below) -
    // "resizing" happens as a side effect of how many panes are visible, not a drag handle.
    // Refuses to hide the last visible pane so there's always at least one editor on screen.
    const visibleCount = ALL_WEB_DEV_FILES.filter((f) => visiblePanes[f]).length
    const togglePane = (file) => {
      // Blocked (trying to hide the last visible pane) shouldn't log as if it succeeded -
      // only report telemetry when the visibility actually changes.
      if (visiblePanes[file] && visibleCount <= 1) return
      setVisiblePanes((prev) => ({ ...prev, [file]: !prev[file] }))
      propsRef.current.onTelemetry?.(CODE_EVENTS.TAB_SWITCH, {
        pane: file,
        visible: !visiblePanes[file],
        timestamp: Date.now(),
      })
    }

    return (
      <div className="ws-editor-container ws-editor-columns" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div className="ws-editor-tabs" style={{
          display: 'flex',
          background: '#fafafa',
          borderBottom: '1px solid #e5e5e5',
          padding: '6px 8px',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {stacked && paneTitle && (
              <span style={{ fontSize: '0.95rem', fontWeight: 700, color: '#171717', whiteSpace: 'nowrap' }}>
                {paneTitle}
              </span>
            )}
            {/* Stacked mode has nothing to show/hide - all three files are permanently
                visible, so the per-file toggle row (JSBin's own affordance) doesn't apply. */}
            {!stacked && (
              <div style={{ display: 'flex', gap: '4px' }}>
                {ALL_WEB_DEV_FILES.map((file) => {
                  const isVisible = visiblePanes[file]
                  return (
                    <button
                      key={file}
                      onClick={() => togglePane(file)}
                      title={isVisible ? `Hide ${WEB_DEV_FILE_LABELS[file]}` : `Show ${WEB_DEV_FILE_LABELS[file]}`}
                      style={{
                        padding: '5px 12px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        backgroundColor: isVisible ? '#171717' : '#ffffff',
                        color: isVisible ? '#ffffff' : '#6b7280',
                        cursor: 'pointer',
                        outline: 'none',
                        textTransform: 'uppercase',
                        letterSpacing: '0.03em',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      {WEB_DEV_FILE_LABELS[file]}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
          <button
            onClick={resetToDefault}
            style={{
              padding: '4px 8px',
              fontSize: '0.725rem',
              fontWeight: 600,
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              backgroundColor: '#ffffff',
              color: '#374151',
              cursor: 'pointer',
              outline: 'none',
              transition: 'all 0.15s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
          >
            Reset Template
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: stacked ? 'column' : 'row', flex: 1, minHeight: 0 }}>
          {ALL_WEB_DEV_FILES.map((file) => {
            // Stacked mode has no visibility toggle - every pane is always shown.
            const isVisible = stacked ? true : visiblePanes[file]
            // First *visible* pane shouldn't have a leading border, regardless of its
            // position in ALL_WEB_DEV_FILES - otherwise hiding HTML would leave a stray
            // leading border on CSS (columns mode only - stacked always shows all three,
            // so the first file (HTML) is always the first visible one).
            const isFirstVisible = isVisible && ALL_WEB_DEV_FILES.slice(0, ALL_WEB_DEV_FILES.indexOf(file)).every((f) => stacked || !visiblePanes[f])
            return (
              <div
                key={`${file}-pane-wrapper`}
                style={{
                  display: isVisible ? 'flex' : 'none',
                  flex: isVisible ? 1 : '0 0 0px',
                  minWidth: stacked ? undefined : 0,
                  minHeight: stacked ? 0 : undefined,
                  flexDirection: 'column',
                  borderLeft: !stacked && !isFirstVisible ? '1px solid #e5e5e5' : 'none',
                  borderTop: stacked && !isFirstVisible ? '1px solid #e5e5e5' : 'none',
                }}
              >
                <div style={{
                  padding: '6px 12px',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  letterSpacing: '0.03em',
                  textTransform: 'uppercase',
                  color: enabledFiles.includes(file) ? '#171717' : '#9ca3af',
                  background: '#fafafa',
                  borderBottom: '1px solid #e5e5e5',
                }}>
                  {WEB_DEV_FILE_LABELS[file]}
                  {!enabledFiles.includes(file) && <span aria-hidden="true" style={{ marginLeft: '4px', fontSize: '0.7rem' }}>🔒</span>}
                </div>
                <div style={{ flex: 1, minHeight: 0 }}>
                  <CodeMirrorPane
                    key={`${file}-${resetKey}`}
                    file={file}
                    initialValue={codesRef.current[file]}
                    editable={enabledFiles.includes(file)}
                    onChange={handlePaneChange(file)}
                    // codesRef.current is already up to date here - handlePaneChange (above)
                    // synchronously updates it via onChange, which CodeMirrorPane always
                    // calls before onTelemetry (see its updateListener). Without this
                    // override, CodeMirrorPane's own onTelemetry call sends `code: newCode` -
                    // just THIS pane's raw text - which breaks replay reconstruction
                    // (useReplayState.js expects the full {html,css,js} JSON, matching what
                    // tabbed mode's telemetry already sends). Pre-existing gap in JSBin's
                    // columns mode, fixed here rather than routed around.
                    onTelemetry={(type, payload) => propsRef.current.onTelemetry?.(type, { ...payload, code: JSON.stringify(codesRef.current) })}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className="ws-editor-container" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {isWebDev && (
        <div className="ws-editor-tabs" style={{
          display: 'flex',
          background: '#fafafa',
          borderBottom: '1px solid #e5e5e5',
          padding: '6px 8px 0 8px',
          gap: '4px',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {paneTitle && (
              <span style={{
                fontSize: '0.95rem',
                fontWeight: 700,
                color: '#171717',
                paddingBottom: '6px',
                whiteSpace: 'nowrap',
              }}>
                {paneTitle}
              </span>
            )}
            <div style={{ display: 'flex', gap: '4px' }}>
            {(hideDisabledTabs ? ALL_WEB_DEV_FILES.filter((f) => enabledFiles.includes(f) || extraReadOnlyFiles.includes(f)) : ALL_WEB_DEV_FILES).map((tab) => {
              const readOnly = !enabledFiles.includes(tab)
              return (
                <button
                  key={tab}
                  onClick={() => {
                    if (tab !== activeTab) {
                      switchTab(tab)
                    }
                  }}
                  title={readOnly ? `${WEB_DEV_FILE_LABELS[tab]} is read-only` : undefined}
                  style={{
                    padding: '6px 14px',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    border: '1px solid #e5e5e5',
                    borderBottom: tab === activeTab ? '1px solid #ffffff' : 'none',
                    borderRadius: '6px 6px 0 0',
                    backgroundColor: tab === activeTab ? '#ffffff' : '#fafafa',
                    color: tab === activeTab ? (readOnly ? '#9ca3af' : '#171717') : '#737373',
                    cursor: 'pointer',
                    marginBottom: '-1px',
                    zIndex: tab === activeTab ? 2 : 1,
                    textTransform: 'uppercase',
                    outline: 'none',
                    transition: 'all 0.15s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  {WEB_DEV_FILE_LABELS[tab]}
                  {readOnly && <span aria-hidden="true" style={{ fontSize: '0.7rem' }}>🔒</span>}
                </button>
              )
            })}
            </div>
          </div>
          <button
            onClick={resetToDefault}
            style={{
              padding: '4px 8px',
              fontSize: '0.725rem',
              fontWeight: 600,
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              backgroundColor: '#ffffff',
              color: '#374151',
              cursor: 'pointer',
              outline: 'none',
              marginRight: '8px',
              marginBottom: '4px',
              transition: 'all 0.15s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
          >
            Reset Template
          </button>
        </div>
      )}
      <div ref={editorRef} style={{ width: '100%', flex: 1, minHeight: 0 }} />
    </div>
  )
}
